import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import * as AuthSelectors from '../../store/auth.selectors';
import { selectAuthError, selectAuthLoading } from '../../store/auth.selectors';
import { ActivatedRoute } from '@angular/router';
import {
  FooterConfig,
  LayoutConfigService,
  UnauthenticatedLayoutComponent,
} from '@angular-monorepo/ui';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { I18N_FACADE, I18nFacade } from '@angular-monorepo/i18n';
import { MessageService } from 'primeng/api';
import { OAuthAuthService } from '../../data-access/oauth-auth.service';

@Component({
  selector: 'lib-login-cloud',
  imports: [CommonModule, TranslatePipe, Button, UnauthenticatedLayoutComponent, NgOptimizedImage],
  templateUrl: './login-cloud.component.html',
  styleUrl: './login-cloud.component.css',
})
export class LoginCloudComponent implements OnInit, OnDestroy {
  @Input() title?: string;
  @Input() backgroundImageUrl?: string;

  footerConfig: FooterConfig = { showDefault: true };
  variant: 'select' | 'toggle' | 'switch' = 'switch';
  loading$!: Observable<boolean>;
  error$!: Observable<string | null>;
  private destroy$ = new Subject<void>();
  private readonly layoutConfigService = inject(LayoutConfigService);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject<I18nFacade>(I18N_FACADE);
  private readonly oauthAuthService = inject(OAuthAuthService, { optional: true });

  constructor() {
    this.loading$ = this.store.select(selectAuthLoading);
    this.error$ = this.store.select(selectAuthError);
  }

  ngOnInit() {
    this.loading$ = this.store.select(AuthSelectors.selectAuthLoading);

    // Verificar si la sesion expiro
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params['sessionExpired'] === 'true') {
        this.messageService.add({
          severity: 'warn',
          summary: this.i18n.instant('auth.sessionExpired.title'),
          detail: this.i18n.instant('auth.sessionExpired.detail'),
          life: 6000,
        });
      }
    });

    this.error$.pipe(takeUntil(this.destroy$)).subscribe((error) => {
      if (error) {
        console.log('💡 LoginCloudComponent - Error en store:', error);
        // Mostrar toast de error
        this.messageService.add({
          severity: 'error',
          summary: this.i18n.instant('auth.errors.title') || 'Error de autenticacion',
          detail: error,
          life: 5000,
        });
      }
    });

    this.layoutConfigService.footerConfig$.pipe(takeUntil(this.destroy$)).subscribe((config) => {
      this.footerConfig = config;

      if (config && config.customElements) {
        const i18nConfig = config.customElements.find((el) => el.content === 'lib-i18n');

        if (i18nConfig && i18nConfig.props?.variant) {
          this.variant = i18nConfig.props.variant as 'select' | 'toggle' | 'switch';
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inicia el flujo de autenticacion OAuth con Azure/Cognito
   */
  onAzureLogin(): void {
    if (!this.oauthAuthService) {
      return;
    }
    // Guardar la URL de destino (dashboard por defecto)
    const targetUrl = '/dashboard';
    this.oauthAuthService.login(targetUrl);
  }
}
